import { useRef, useState } from 'react';
import { Box, IconButton, Modal } from '@mui/material';
import { MdChevronLeft, MdChevronRight, MdClose } from 'react-icons/md';

interface DestinationImagesCarouselProps {
    images: string[];
    destination: string;
}

export const DestinationImagesCarousel = ({ images, destination }: DestinationImagesCarouselProps) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    if (!images || images.length === 0) {
        return null;
    }

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 400; // Scroll by 2 images at a time (200px * 2)
            const currentScroll = scrollContainerRef.current.scrollLeft;
            const targetScroll = direction === 'left'
                ? currentScroll - scrollAmount
                : currentScroll + scrollAmount;

            scrollContainerRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
        }
    };

    const openModal = (index: number) => {
        setSelectedImageIndex(index);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    const navigateImage = (direction: 'prev' | 'next') => {
        if (direction === 'prev') {
            setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
        } else {
            setSelectedImageIndex((prev) => (prev + 1) % images.length);
        }
    };

    return (
        <div className="space-y-4 w-full max-w-full">
            <div className="relative w-full">
                {/* Navigation Arrow - Left */}
                {images.length > 3 && (
                    <IconButton
                        onClick={() => scroll('left')}
                        sx={{
                            position: 'absolute',
                            left: { xs: '0px', md: '-20px' }, // Safely at edge
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            width: '40px',
                            height: '40px',
                            zIndex: 10,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            },
                        }}
                    >
                        <MdChevronLeft style={{ fontSize: '28px' }} />
                    </IconButton>
                )}

                {/* Scrollable Container */}
                <div
                    ref={scrollContainerRef}
                    className="flex gap-4 overflow-x-auto pb-2 scroll-smooth justify-center"
                    style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#187A85 #e2e8f0'
                    }}
                >
                    {images.map((image, index) => (
                        <button
                            key={index}
                            onClick={() => openModal(index)}
                            className="flex-shrink-0 w-[200px] h-[200px] rounded-xl overflow-hidden bg-slate-100 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#187A85]"
                        >
                            <img
                                src={image}
                                alt={`${destination} - Image ${index + 1}`}
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                    e.currentTarget.src = 'https://via.placeholder.com/200?text=Image+Not+Available';
                                }}
                            />
                        </button>
                    ))}
                </div>

                {/* Navigation Arrow - Right */}
                {images.length > 3 && (
                    <IconButton
                        onClick={() => scroll('right')}
                        sx={{
                            position: 'absolute',
                            right: { xs: '0px', md: '-20px' }, // Safely at edge
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            width: '40px',
                            height: '40px',
                            zIndex: 10,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            },
                        }}
                    >
                        <MdChevronRight style={{ fontSize: '28px' }} />
                    </IconButton>
                )}
            </div>

            {/* Lightbox Modal */}
            <Modal
                open={modalOpen}
                onClose={closeModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        outline: 'none',
                    }}
                    className='p-8'
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button */}
                    <IconButton
                        onClick={closeModal}
                        sx={{
                            position: 'absolute',
                            top: '-20px',
                            right: '0',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            color: '#333',
                            width: '44px',
                            height: '44px',
                            zIndex: 20,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                            },
                        }}
                    >
                        <MdClose style={{ fontSize: '28px' }} />
                    </IconButton>

                    {/* Image Counter */}
                    <div className="absolute top-[-20px] left-0 bg-white/90 text-slate-800 px-4 py-2 rounded-lg text-sm font-semibold">
                        {selectedImageIndex + 1} / {images.length}
                    </div>

                    {/* Previous Arrow */}
                    {images.length > 1 && (
                        <IconButton
                            onClick={() => navigateImage('prev')}
                            sx={{
                                position: 'absolute',
                                left: { xs: '10px', md: '-60px' },
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                color: '#333',
                                width: '48px',
                                height: '48px',
                                zIndex: 20,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                },
                            }}
                        >
                            <MdChevronLeft style={{ fontSize: '32px' }} />
                        </IconButton>
                    )}

                    {/* Main Image */}
                    <img
                        src={images[selectedImageIndex]}
                        alt={`${destination} - Full size ${selectedImageIndex + 1}`}
                        className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
                        onError={(e) => {
                            e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                        }}
                    />

                    {/* Next Arrow */}
                    {images.length > 1 && (
                        <IconButton
                            onClick={() => navigateImage('next')}
                            sx={{
                                position: 'absolute',
                                right: { xs: '10px', md: '-60px' },
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                color: '#333',
                                width: '48px',
                                height: '48px',
                                zIndex: 20,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                },
                            }}
                        >
                            <MdChevronRight style={{ fontSize: '32px' }} />
                        </IconButton>
                    )}
                </Box>
            </Modal>

            {/* Custom scrollbar styles */}
            <style>{`
                .overflow-x-auto::-webkit-scrollbar {
                    height: 8px;
                }
                .overflow-x-auto::-webkit-scrollbar-track {
                    background: #e2e8f0;
                    border-radius: 4px;
                }
                .overflow-x-auto::-webkit-scrollbar-thumb {
                    background: #187A85;
                    border-radius: 4px;
                }
                .overflow-x-auto::-webkit-scrollbar-thumb:hover {
                    background: #0f5a62;
                }
            `}</style>
        </div>
    );
};
